import { Injectable, Type } from '@angular/core';
import { BehaviorSubject, Observable, from, map, of, switchMap, distinctUntilChanged } from 'rxjs';

// See ./NG-COMPONENT-OUTLET-SLOTS.md for how to define/consume/register a slot.
export type ComponentSlotLoader = () => Promise<Type<unknown>>;

// Lets a product module (Skoot, ABC, Cavex, ...) inject its own component into a
// named extension point on a shared/core page without the core page importing
// or knowing about that product's components. A product's bootstrap service
// calls register() during initializeForProduct(); the core page subscribes to the
// component via resolve$() and renders it with NgComponentOutlet.
//
// register() takes a LOADER (() => import('./x.component').then(m => m.X)), not a live
// class reference. A product's *BootstrapService is root-provided and constructed at app
// startup, so anything it imports statically (including a directly-passed component class)
// gets pulled into the eager main bundle by the bundler - the dynamic import keeps the
// component, and everything it imports, inside that product's own lazy chunk until a page
// actually asks for this slot.
//
// resolve$() is reactive (not a one-shot snapshot) because registration itself is async:
// *BootstrapService only registers once ProductBootstrapService's settings$ HTTP call
// resolves, which the app initializer does not await. A page whose component constructs
// before that response lands must still pick up the registration once it happens, rather
// than permanently reading an empty registry.
@Injectable({
  providedIn: 'root',
})
export class ComponentSlotRegistryService {
  private readonly slotsSubject = new BehaviorSubject<Map<string, ComponentSlotLoader>>(
    new Map()
  );

  register(slotName: string, loader: ComponentSlotLoader): void {
    const next = new Map(this.slotsSubject.value);
    next.set(slotName, loader);
    this.slotsSubject.next(next);
  }

  resolve$(slotName: string): Observable<Type<unknown> | null> {
    return this.slotsSubject.pipe(
      map((slots) => slots.get(slotName) ?? null),
      distinctUntilChanged(),
      switchMap((loader) => (loader ? from(loader()) : of(null)))
    );
  }
}
